import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/modal';
import { Tag } from '../../hooks/useTags';
import { TagGroup, use2026TagGroups } from '../../hooks/useTagGroups';
import { toast } from 'react-toastify';

interface TagSelectorModalProps {
  title: string;
  tags: Tag[];
  selectedTagIds?: number[];
  maxSelectable?: number;
  lockedTagIds?: number[];
  onSave: (tagIds: number[]) => void;
  onClose: () => void;
}

const getGroupTag = (group: TagGroup): Tag | undefined => {
  if (!group.tags || group.tags.length === 0) return undefined;
  return group.tags.find((tag) => tag.name === group.name);
};

const TagSelectorModal: React.FC<TagSelectorModalProps> = ({
  title,
  tags,
  selectedTagIds,
  maxSelectable,
  lockedTagIds,
  onSave,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(selectedTagIds || []);
  const { data: tagGroups, isLoading: isTagGroupsLoading } = use2026TagGroups();

  useEffect(() => {
    setSelectedIds(selectedTagIds || []);
  }, [selectedTagIds]);

  const tagLookup = useMemo(() => {
    const map = new Map<number, Tag>();
    tagGroups?.forEach((group) => {
      const groupTag = getGroupTag(group);
      if (groupTag) map.set(groupTag.id, groupTag);
    });
    tags.forEach((tag) => {
      if (!map.has(tag.id)) {
        map.set(tag.id, tag);
      }
    });
    return map;
  }, [tagGroups, tags]);

  const selectedTagBadges = useMemo(
    () =>
      selectedIds.map((id) => ({
        id,
        tag: tagLookup.get(id),
        isLocked: !!lockedTagIds?.includes(id),
      })),
    [lockedTagIds, selectedIds, tagLookup],
  );

  const handleGroupToggle = (group: TagGroup) => {
    const groupTag = getGroupTag(group);
    if (!groupTag) {
      return;
    }

    const tagId = groupTag.id;
    if (lockedTagIds?.includes(tagId)) {
      return;
    }

    const isSelected = selectedIds.includes(tagId);
    if (isSelected) {
      setSelectedIds((prev) => prev.filter((id) => id !== tagId));
    } else {
      const next = [...selectedIds, tagId];
      const selectableCount = lockedTagIds ? next.filter((id) => !lockedTagIds.includes(id)).length : next.length;
      if (maxSelectable && selectableCount > maxSelectable) {
        toast.error(`Maximum of ${maxSelectable} tags allowed.`);
        return;
      }
      setSelectedIds(next);
    }
  };

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          onClick={(event) => event.stopPropagation()}
          className="inline-block transform overflow-hidden bg-white px-6 pb-6 pt-6 align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle"
        >
          <div className="flex items-center justify-between pb-4">
            <h3 className="blue-primary text-2xl font-normal">{title}</h3>
            <button className="text-blue-primary text-sm font-semibold" onClick={onClose}>
              Close
            </button>
          </div>
          {maxSelectable && <div className="pb-3 text-xs text-gray-500">Select up to {maxSelectable} tags.</div>}
          {selectedTagBadges.length > 0 && (
            <div className="pb-4">
              <div className="text-sm font-semibold text-gray-600">Selected Tags</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedTagBadges.map(({ id, tag, isLocked }) => (
                  <span
                    key={id}
                    className={`rounded-md border px-2 py-1 text-xs ${
                      isLocked ? 'border-gray-300 text-gray-500' : 'border-blue-primary text-blue-primary'
                    }`}
                  >
                    {tag?.name ?? `Tag ${id}`}
                    {isLocked && <span className="text-gray-400"> (locked)</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {isTagGroupsLoading && <div className="pb-4 text-sm text-gray-500">Loading tag groups...</div>}
          {!isTagGroupsLoading && tagGroups && tagGroups.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {tagGroups.map((group) => {
                const groupTag = getGroupTag(group);
                if (!groupTag) return null;
                const isSelected = selectedIds.includes(groupTag.id);
                const isLocked = !!lockedTagIds?.includes(groupTag.id);
                return (
                  <button
                    key={group.id}
                    type="button"
                    className={`rounded-md border px-4 py-3 text-left text-sm font-semibold transition ${
                      isSelected
                        ? 'border-blue-primary text-blue-primary bg-blue-50'
                        : 'hover:border-blue-primary hover:text-blue-primary border-gray-200 bg-white text-gray-700'
                    } ${isLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                    onClick={() => handleGroupToggle(group)}
                    disabled={isLocked}
                  >
                    <div>{group.name}</div>
                  </button>
                );
              })}
            </div>
          )}
          {!isTagGroupsLoading && (!tagGroups || tagGroups.length === 0) && (
            <div className="text-sm text-gray-500">No tag groups available.</div>
          )}
          <div className="flex justify-end pt-6">
            <button
              className="bg-blue-primary h-8 w-20 rounded-sm text-center text-sm text-white"
              onClick={() => onSave(selectedIds)}
            >
              Save
            </button>
          </div>
        </div>
      }
    />
  );
};

export default TagSelectorModal;
